<script>
import { mapState, mapActions } from 'vuex';
import { getMenus } from '@/utils/api/contentful';
import { formatMenus } from '@/utils/formatData';
import { SwIconButton } from '@getswish/design-library-web';
import MenuItem from './menu/MenuItem.vue';
import MenuItemSecondary from './menu/MenuItemSecondary.vue';
import MenuItemApiReference from './menu/MenuItemApiReference.vue';
import NavButton from '../NavButton.vue';

export default {
  name: 'AppSidebar',

  components: {
    MenuItem,
    MenuItemSecondary,
    MenuItemApiReference,
    SwIconButton,
    NavButton,
  },

  props: {
    isApiPage: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      mainMenu: null,
      secondaryMenu: null,
    };
  },

  computed: {
    ...mapState('settings', ['isSidebarOpen']),
  },

  created() {
    this.setMenus();
  },

  methods: {
    ...mapActions('menu', ['setMenuStructure']),
    ...mapActions('settings', ['closeSidebar']),

    async setMenus() {
      const identifier = this.isApiPage ? 'api' : '';

      try {
        const page = await getMenus(identifier);
        const { mainMenu, secondaryMenu } = formatMenus(page.fields);
        this.mainMenu = mainMenu;
        this.secondaryMenu = secondaryMenu;

        this.setMenuStructure({
          mainMenu: this.mainMenu,
          parent: identifier,
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<template>
  <aside class="sidebar" :class="{ api: isApiPage, show: isSidebarOpen }">
    <SwIconButton
      icon-name="cross"
      variant="secondary"
      class="icon-button"
      @click="closeSidebar()"
      aria-label="Close Sidebar"
    />

    <nav v-if="mainMenu && isApiPage">
      <ul>
        <li v-for="item in mainMenu" :key="item.sys.id">
          <MenuItemApiReference :item="item" />
        </li>
      </ul>
    </nav>

    <nav v-else-if="mainMenu">
      <ul>
        <li v-for="item in mainMenu" :key="item.sys.id">
          <MenuItem :item="item" parent-path="" :level="0" />
        </li>
      </ul>
    </nav>

    <nav class="secondary-menu" v-if="secondaryMenu">
      <ul>
        <li v-for="item in secondaryMenu" :key="item.sys.id">
          <MenuItemSecondary :item="item" />
        </li>
      </ul>
    </nav>

    <div class="buttons-mobile">
      <router-link v-if="isApiPage" :to="{ name: 'home-documentation' }" @click="closeSidebar">
        <NavButton variant="documentation" />
      </router-link>

      <router-link v-else :to="{ name: 'home-api-reference' }" @click="closeSidebar">
        <NavButton variant="apiReference" />
      </router-link>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  right: -$left-sidebar-width;
  width: $left-sidebar-width;
  background: #fff;
  border-right: 1px solid $separator-color;
  text-align: left;
  padding-top: $space-40;
  transition: 0.14s linear;
  overflow-y: auto;
  z-index: 998;

  @include tablet-large {
    transition: none;
    padding-top: 0;
  }

  &.api {
    background-color: $background-color-secondary;
  }

  &.show {
    right: 0;
  }

  @include tablet-large {
    top: $topbar-height-large;
    left: 0;
  }

  .icon-button {
    padding: 0;
    position: absolute;
    top: $space-16;
    right: $space-16;

    @include tablet-large {
      display: none;
    }
  }

  nav {
    padding: $space-16 0;

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      :deep(li) {
        list-style: none;
      }
    }
  }
}

.secondary-menu {
  border-top: 1px solid $separator-color;

  :deep(a) {
    padding-left: 1.5rem;
  }
}

.buttons-mobile {
  border-top: 1px solid $separator-color;
  padding: 1.5rem;

  @include tablet-large {
    display: none;
  }
}

@media print {
  .sidebar {
    display: none;
  }
}
</style>
